import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Toaster, toast } from 'react-hot-toast';

// Components
import HomePage from '../pages/HomePage/HomePage';
import { FilterSidebar } from '../components/filters/FilterSidebar';
import { FavoritesSidebar } from '../components/favorites/FavoritesSidebar';
import { Icon, BinaryToggle, Button } from '../pages/DesignSystem';
import LogoSVG from '../pages/DesignSystem/primitives/LogoSVG';

// Services
import { locationCache } from '../services/LocationCacheService';

// Auth
import { useAuth } from '../contexts/AuthContext';

// Stores
import { useHiddenEvents, useViewMode } from '../stores/appStore';

// Styles
import './styles/layout-variables.css';
import './styles/tourist-desktop-layout.css';

/**
 * TouristDesktopLayout - Layout desktop pour le mode touriste
 *
 * Affiche la HomePage existante avec des contrôles flottants:
 * - Logo + Menu dropdown (haut gauche)
 * - FAB Favoris (bas gauche) qui ouvre un panel
 *
 * Le BottomNav de HomePage est masqué via CSS sur desktop.
 * Les filtres sont synchronisés avec HomePage via custom events.
 */
interface FilterState {
  selectedTags: string[];
  searchText: string;
  searchCity: string;
  dateFilter: string[];
  timeFilter: string[];
  distance: number;
  currentLocation?: { latitude: number; longitude: number };
}

const TouristDesktopLayout: React.FC = () => {
  // View mode - Zustand store (synced with HomePage)
  const { viewMode, setViewMode } = useViewMode();

  // Auth and navigation
  const { user } = useAuth();
  const [, navigate] = useLocation();

  // Filter state - synced with HomePage via events
  const [filterState, setFilterState] = useState<FilterState>({
    selectedTags: [],
    searchText: '',
    searchCity: '',
    dateFilter: [],
    timeFilter: [],
    distance: 5,
    currentLocation: undefined
  });

  // Panel visibility states
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);

  // Modal states (same as mobile MenuDrawer)
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAmbassadorModal, setShowAmbassadorModal] = useState(false);

  // Email copy handler for desktop
  const handleEmailClick = useCallback((email: string, subject?: string) => {
    navigator.clipboard.writeText(email).then(() => {
      toast.success(`Email copied: ${email}`, {
        icon: '📋',
        duration: 3000
      });
    }).catch(() => {
      // Fallback to mailto if clipboard fails
      const mailto = subject ? `mailto:${email}?subject=${encodeURIComponent(subject)}` : `mailto:${email}`;
      window.location.href = mailto;
    });
  }, []);

  // Favorites count for badge
  const [favoritesCount, setFavoritesCount] = useState(0);

  // Hidden events for Undo functionality
  const { undoLastRemoval, hiddenEventsDetails } = useHiddenEvents();
  const dismissedCount = Object.values(hiddenEventsDetails)
    .filter((e: { reason: string }) => e.reason === 'dismiss').length;

  // Load and sync favorites count (same as BottomNav)
  useEffect(() => {
    const updateFavoritesCount = async () => {
      try {
        const count = await locationCache.getFavoritesCount();
        setFavoritesCount(count);
      } catch (error) {
        console.error('❌ [TouristDesktopLayout] Erreur récupération compteur favoris:', error);
        setFavoritesCount(0);
      }
    };

    updateFavoritesCount();

    const handleFavoritesUpdate = () => {
      updateFavoritesCount();
    };

    window.addEventListener('favoritesChanged', handleFavoritesUpdate);
    return () => {
      window.removeEventListener('favoritesChanged', handleFavoritesUpdate);
    };
  }, []);

  // Refs for click outside detection
  const menuRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const favoritesRef = useRef<HTMLDivElement>(null);

  // Listen for filter state updates from HomePage
  useEffect(() => {
    const handleFilterStateUpdate = (event: CustomEvent<FilterState>) => {
      setFilterState(event.detail);
    };

    window.addEventListener('filterStateUpdate', handleFilterStateUpdate as EventListener);

    // Request initial filter state from HomePage
    window.dispatchEvent(new CustomEvent('requestFilterState'));

    return () => {
      window.removeEventListener('filterStateUpdate', handleFilterStateUpdate as EventListener);
    };
  }, []);

  // Filter change handlers - dispatch events to HomePage
  const handleSearchTextChange = useCallback((text: string) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'searchText', value: text }
    }));
  }, []);

  const handleSearchCityChange = useCallback((city: string) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'searchCity', value: city }
    }));
  }, []);

  const handleLocationChange = useCallback((location: { latitude: number; longitude: number; shouldCenter?: boolean }) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'location', value: location }
    }));
  }, []);

  const handleDistanceChange = useCallback((distance: number) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'distance', value: distance }
    }));
  }, []);

  const handleDateChange = useCallback((dates: string[]) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'dateFilter', value: dates }
    }));
  }, []);

  const handleTimeChange = useCallback((times: string[]) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'timeFilter', value: times }
    }));
  }, []);

  const handleTagSelect = useCallback((tagId: string) => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'tagSelect', value: tagId }
    }));
  }, []);

  const handleReset = useCallback(() => {
    window.dispatchEvent(new CustomEvent('filterChange', {
      detail: { type: 'reset' }
    }));
  }, []);

  // Close panels when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setIsFiltersOpen(false);
      }
      if (favoritesRef.current && !favoritesRef.current.contains(event.target as Node)) {
        setIsFavoritesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="tourist-desktop-layout">
      {/* HomePage as main content */}
      <div className="tourist-desktop-layout__content">
        <HomePage />
      </div>

      {/* Top Left: Logo + Menu + View Toggle */}
      <div className="tourist-desktop-layout__top-bar">
        <div className="tourist-desktop-layout__logo-menu" ref={menuRef}>
          <button
            className="tourist-desktop-layout__logo-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Menu"
          >
            <LogoSVG
              variant="what2Do"
              size="sm"
              color="var(--color-primary)"
            />
            <Icon name={isMenuOpen ? "ChevronUp" : "ChevronDown"} size={16} />
          </button>

          {/* Dropdown Menu - Same items as mobile MenuDrawer */}
          <div className={`tourist-desktop-layout__menu-dropdown ${isMenuOpen ? 'tourist-desktop-layout__menu-dropdown--open' : ''}`}>
            <button
              className="tourist-desktop-layout__menu-link"
              onClick={() => {
                setIsMenuOpen(false);
                if (user?.uid) {
                  navigate('/organizer');
                } else {
                  sessionStorage.setItem('redirectAfterLogin', '/organizer');
                  navigate('/login');
                }
              }}
            >
              <Icon name="Megaphone" size={18} />
              <span>Post an Event</span>
            </button>
            <button
              className="tourist-desktop-layout__menu-link"
              onClick={() => {
                setIsMenuOpen(false);
                setShowAmbassadorModal(true);
              }}
            >
              <Icon name="Users" size={18} />
              <span>Ambassador Program</span>
            </button>
            <button
              className="tourist-desktop-layout__menu-link"
              onClick={() => window.open('https://www.instagram.com/what2do.today/', '_blank')}
            >
              <Icon name="Instagram" size={18} />
              <span>Follow us on Instagram</span>
            </button>
            <button
              className="tourist-desktop-layout__menu-link"
              onClick={() => {
                setIsMenuOpen(false);
                setShowContactModal(true);
              }}
            >
              <Icon name="MessageSquare" size={18} />
              <span>Contact & Feedback</span>
            </button>
            <button
              className="tourist-desktop-layout__menu-link"
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/about');
              }}
            >
              <Icon name="Info" size={18} />
              <span>About</span>
            </button>
            <button
              className="tourist-desktop-layout__menu-link"
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/legal');
              }}
            >
              <Icon name="Shield" size={18} />
              <span>Legal & Privacy Policy</span>
            </button>
          </div>
        </div>

        {/* View Toggle - Map/List + Undo */}
        <div className="tourist-desktop-layout__view-controls">
          <div className="tourist-desktop-layout__view-toggle-wrapper">
            <BinaryToggle
              leftLabel="List"
              rightLabel="Map"
              value={viewMode === 'map'}
              onChange={(isMap) => setViewMode(isMap ? 'map' : 'list')}
            />
          </div>
          {/* Undo button - visible only in list view */}
          {viewMode === 'list' && (
            <button
              className="tourist-desktop-layout__undo-btn"
              onClick={() => undoLastRemoval()}
              disabled={dismissedCount === 0}
              aria-label="Undo last dismissed event"
            >
              <Icon name="RotateCcw" size={16} />
              <span>Undo</span>
            </button>
          )}
        </div>
      </div>

      {/* Bottom Left: Filter + Favorites FABs */}
      <div className="tourist-desktop-layout__bottom-controls">
        {/* Filter Panel (expands upward) */}
        <div className="tourist-desktop-layout__filter-container" ref={filtersRef}>
          <div className={`tourist-desktop-layout__filter-panel ${isFiltersOpen ? 'tourist-desktop-layout__filter-panel--open' : ''}`}>
            <FilterSidebar
              viewMode={viewMode}
              searchText={filterState.searchText}
              onSearchTextChange={handleSearchTextChange}
              searchCity={filterState.searchCity}
              setSearchCity={handleSearchCityChange}
              currentLocation={filterState.currentLocation}
              onLocationChange={handleLocationChange}
              distance={filterState.distance}
              onDistanceChange={handleDistanceChange}
              selectedDates={filterState.dateFilter}
              onDateChange={handleDateChange}
              selectedTimes={filterState.timeFilter}
              onTimeChange={handleTimeChange}
              selectedTags={filterState.selectedTags}
              onTagSelect={handleTagSelect}
              onReset={handleReset}
            />
          </div>
          <button
            className={`tourist-desktop-layout__fab tourist-desktop-layout__fab--filter ${isFiltersOpen ? 'tourist-desktop-layout__fab--active' : ''}`}
            onClick={() => {
              setIsFiltersOpen(!isFiltersOpen);
              if (!isFiltersOpen) setIsFavoritesOpen(false);
            }}
            aria-expanded={isFiltersOpen}
            aria-label="Filters"
          >
            <Icon name="SlidersHorizontal" size={20} />
          </button>
        </div>

        {/* Favorites Panel (expands upward) */}
        <div className="tourist-desktop-layout__favorites-container" ref={favoritesRef}>
          <div className={`tourist-desktop-layout__favorites-panel ${isFavoritesOpen ? 'tourist-desktop-layout__favorites-panel--open' : ''}`}>
            <FavoritesSidebar />
          </div>
          <button
            className={`tourist-desktop-layout__fab tourist-desktop-layout__fab--favorites ${isFavoritesOpen ? 'tourist-desktop-layout__fab--active' : ''}`}
            onClick={() => {
              setIsFavoritesOpen(!isFavoritesOpen);
              if (!isFavoritesOpen) setIsFiltersOpen(false);
            }}
            aria-expanded={isFavoritesOpen}
            aria-label="Favorites"
          >
            <Icon name="Heart" size={20} />
            {favoritesCount > 0 && (
              <span className="tourist-desktop-layout__fab-badge">{favoritesCount}</span>
            )}
          </button>
        </div>
      </div>

      <Toaster
        position="top-right"
        toastOptions={{
          className: 'tourist-toast',
          duration: 3000
        }}
      />

      {/* Contact Modal - Same as mobile MenuDrawer */}
      {showContactModal && (
        <div
          className="tourist-desktop-layout__modal-overlay"
          onClick={() => setShowContactModal(false)}
        >
          <div className="tourist-desktop-layout__modal" onClick={e => e.stopPropagation()}>
            <h3 className="tourist-desktop-layout__modal-title">
              Get in touch! <LogoSVG variant="singeOrange" size="lg" />
            </h3>
            <div className="tourist-desktop-layout__modal-buttons">
              <Button
                variant="primary"
                onClick={() => handleEmailClick('nico@what2do.today')}
              >
                <Icon name="Copy" size={20} />
                Copy email address
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.open("https://forms.gle/WsLPhinC1fS5eYJcA", "_blank")}
              >
                <Icon name="MessageSquare" size={20} />
                Share your feedback
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ambassador Modal - Same as mobile MenuDrawer */}
      {showAmbassadorModal && (
        <div
          className="tourist-desktop-layout__modal-overlay"
          onClick={() => setShowAmbassadorModal(false)}
        >
          <div className="tourist-desktop-layout__modal" onClick={e => e.stopPropagation()}>
            <h3 className="tourist-desktop-layout__modal-title">
              Ambassador Program <LogoSVG variant="singeOrange" size="lg" />
            </h3>
            <p className="tourist-desktop-layout__modal-description">
              This program is currently under construction. Contact us by email to learn more about becoming an ambassador!
            </p>
            <div className="tourist-desktop-layout__modal-buttons">
              <Button
                variant="primary"
                onClick={() => handleEmailClick('nico@what2do.today', 'Ambassador Program Inquiry')}
              >
                <Icon name="Copy" size={20} />
                Copy email address
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TouristDesktopLayout;
